import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { Button, Modal } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import SelectField from 'components/SelectField';
import { DataRoomRfiTemplate } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { useNavigate } from 'react-router-dom';
import { getDataRoomRoute } from 'utils';

import './ChooseTemplateModal.styles.scss';

type ChooseTemplateModalProps = {
  dataRoomId: string;
  rfiTemplates: DataRoomRfiTemplate[];
} & ModalProps;

const ChooseTemplateModal: React.ForwardRefRenderFunction<
  ModalRef,
  ChooseTemplateModalProps
> = (props, ref) => {
  const { className, dataRoomId, rfiTemplates, ...rest } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('choose-template-modal', className);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const signingsOptions = useMemo(
    () =>
      rfiTemplates.map(({ _id, name }) => ({
        value: _id,
        label: name,
      })),
    [rfiTemplates],
  );

  const onSelect = useCallback(
    (templateId: string) => {
      modal?.close();
      navigate(`${getDataRoomRoute(dataRoomId, 'rfi/create')}/${templateId}`);
    },
    [modal, dataRoomId, navigate],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('createFromTemplate')}
      {...rest}
    >
      <Form
        onSubmit={({ templateId }) => onSelect(templateId)}
        render={({ values: { templateId }, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Field
              name="templateId"
              options={signingsOptions}
              component={SelectField}
            />

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('close')}
              </Button>
              <Button type="submit" disabled={!templateId}>
                {t('select')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(ChooseTemplateModal);
